import { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Select,
  Text,
  Button,
  Heading,
  HStack,
  Box,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import api from "../services/api";

const MotionBox = motion(Box);

export default function ContactModal({ isOpen, onClose }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email:"",
    type: "",
    interest: "",
  });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setDone(false);
      setError("");
    }, 300);
  };

  const handleSubmit = async () => {
    // Reset error state
    setError("");

    // Validate required fields
    if (!form.name || !form.phone || !form.email) {
      setError("Please fill in all required fields (Name, Phone, and Email).");
      return;
    }

    // Basic validation for email or phone
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[\d\s\-\+\(\)]+$/;
    if (!emailRegex.test(form.email) && !phoneRegex.test(form.phone)) {
      setError("Please enter a valid email address or phone number.");
      return;
    }

    setLoading(true);
    try {
      // Map form fields to match API expectations
      const payload = {
        fullname: form.name,
        email: form.email,
        phone: form.phone,
        type: form.type,
        interest: form.interest,
      };
      const response = await api.createConnectAgent(payload);
      console.log("Contact modal response:", response);
      setDone(true);
      // Reset form data
      setForm({
        name: "",
        phone: "",
        email: "",
        type: "",
        interest: "",
      });
    } catch (err) {
      console.error("Contact modal error:", err);
      const errorMessage =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        "An error occurred. Please try again later.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" isCentered>
      <ModalOverlay backdropFilter="blur(12px)" bg="rgba(0,0,0,0.85)" />
      <ModalContent
        bg="brand.charcoal"
        borderRadius="0"
        border="1px solid rgba(46,46,46,0.8)"
        mx={4}
      >
        <ModalHeader
          borderBottom="1px solid rgba(46,46,46,0.6)"
          pb={6}
          pt={8}
          px={8}
        >
          <HStack mb={2} spacing={3}>
            <Box w="20px" h="1px" bg="brand.coral" />
            <Text
              fontSize="xs"
              letterSpacing="0.3em"
              textTransform="uppercase"
              color="brand.coral"
              fontWeight="600"
            >
              Get Connected
            </Text>
          </HStack>
          <Heading
            fontFamily="'Playfair Display', serif"
            fontSize="2xl"
            fontWeight="400"
            color="brand.cream"
            lineHeight="1.3"
          >
            Let's connect you with{" "}
            <Text as="span" fontStyle="italic" color="brand.coral">
              the right agent.
            </Text>
          </Heading>
          <Text
            fontSize="sm"
            color="rgba(245,240,235,0.5)"
            mt={2}
            fontFamily="'DM Sans', sans-serif"
          >
            Tell us a little about your needs, and our team will pass your
            details to a trusted partner.
          </Text>
        </ModalHeader>
        <ModalCloseButton color="brand.muted" top={4} right={4} />
        <ModalBody px={8} py={8}>
          {!done ? (
            <VStack spacing={5}>
              {error && (
                <Alert
                  status="error"
                  borderRadius="0"
                  bg="rgba(220,38,38,0.1)"
                  border="1px solid rgba(220,38,38,0.3)"
                >
                  <AlertIcon color="brand.coral" />
                  <Text color="brand.coral" fontSize="sm">
                    {error}
                  </Text>
                </Alert>
              )}
              <FormControl>
                <FormLabel
                  fontSize="xs"
                  letterSpacing="0.2em"
                  textTransform="uppercase"
                  color="brand.muted"
                  mb={2}
                >
                  Name *
                </FormLabel>
                <Input
                  variant="dark"
                  placeholder="Your full name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  disabled={loading}
                />
              </FormControl>
              <FormControl>
                <FormLabel
                  fontSize="xs"
                  letterSpacing="0.2em"
                  textTransform="uppercase"
                  color="brand.muted"
                  mb={2}
                >
                  Phone *
                </FormLabel>
                <Input
                  variant="dark"
                  placeholder="Phone number"
                  value={form.phone}
                  onChange={(e) =>
                    setForm({ ...form, phone: e.target.value })
                  }
                  disabled={loading}
                />
              </FormControl>
               <FormControl>
                <FormLabel
                  fontSize="xs"
                  letterSpacing="0.2em"
                  textTransform="uppercase"
                  color="brand.muted"
                  mb={2}
                >
                  Email*
                </FormLabel>
                <Input
                  variant="dark"
                  placeholder="Email address"
                  value={form.email}
                  onChange={(e) =>
                    setForm({ ...form, email: e.target.value })
                  }
                  disabled={loading}
                />
              </FormControl>
              <FormControl>
                <FormLabel
                  fontSize="xs"
                  letterSpacing="0.2em"
                  textTransform="uppercase"
                  color="brand.muted"
                  mb={2}
                >
                  I Am A *
                </FormLabel>
                <Select
                  variant="dark"
                  placeholder="Select your role..."
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  disabled={loading}
                >
                  <option value="seller">Seller</option>
                  <option value="buyer">Buyer</option>
                  <option value="landlord">Landlord</option>
                  <option value="tenant">Tenant</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel
                  fontSize="xs"
                  letterSpacing="0.2em"
                  textTransform="uppercase"
                  color="brand.muted"
                  mb={2}
                >
                  Property Location / Type{" "}
                  <Text as="span" opacity={0.4}>
                    (optional)
                  </Text>
                </FormLabel>
                <Input
                  variant="dark"
                  placeholder="e.g. 3-bed apartment, Sandton"
                  value={form.interest}
                  onChange={(e) =>
                    setForm({ ...form, interest: e.target.value })
                  }
                  disabled={loading}
                />
              </FormControl>
              <Text
                fontSize="xs"
                color="rgba(245,240,235,0.3)"
                lineHeight="1.7"
                pt={1}
              >
                By submitting, you agree to be contacted by a Reliable Touch
                Services representative regarding your inquiry.
              </Text>
              <Button
                variant="coral"
                w="full"
                h="52px"
                mt={1}
                onClick={handleSubmit}
                isLoading={loading}
                loadingText="Submitting..."
                disabled={loading}
              >
                Submit Request
              </Button>
            </VStack>
          ) : (
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              textAlign="center"
              py={10}
            >
              <Text fontSize="5xl" mb={4}>
                ✓
              </Text>
              <Heading
                fontFamily="'Playfair Display', serif"
                fontSize="xl"
                color="brand.coral"
                mb={3}
              >
                Thank You!
              </Heading>
              <Text color="rgba(245,240,235,0.6)" lineHeight="1.8">
                One of our professional agents will be in touch shortly.
              </Text>
            </MotionBox>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
